import { AxiosRequestConfig } from "axios";
import { SpaceIdType, SpaceType } from "../../types/space";
import { SpaceStatusType } from "../../types/space/enum";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { ResponseError } from "../../api/types";
import { RequestPayloadGetSpaces, SpaceRequests } from "./requests";

interface SpaceQueryOptions<TResponseData = unknown, TPayload = undefined> {
  /** 요청을 보낼 때 필요한 정보 */
  payload: TPayload;
  /** axios config */
  config?: AxiosRequestConfig;
  /** queryKey와 queryFn을 제외한 쿼리 옵션 */
  options?: Omit<UseQueryOptions<TResponseData, ResponseError>, "queryKey" | "queryFn">;
}

export const spaceQueryKeys = {
  all: ["space"],
  // REVIEW: 키가 시나리오에 따라 각각의 entity에서 서로 다른 방식으로 적용되면 혼선을 가져올 수 있음
  // key에도 요청 context에 대한 힌트를 줌으로써 좀 더 직관적으로 invalidation을 처리할 수 있음
  // - get one의 경우에는 늘 ID가 들어가고, getAll의 경우에는 아이디가 필요없음
  // - get all의 경우에는 검색할 수 있는 query param이 들어감
  getAllByParams: (params: RequestPayloadGetSpaces["params"]) => [...spaceQueryKeys.all, params],
  // ASIS
  byId: (id: SpaceIdType) => [...spaceQueryKeys.all, { id }],
  status: (itemStatus: SpaceStatusType) => [...spaceQueryKeys.all, { itemStatus }],
} as const;

export const useGetSpaces = ({
  payload: params,
  options,
  config,
}: SpaceQueryOptions<SpaceType[], RequestPayloadGetSpaces["params"]>) => {
  return useQuery<SpaceType[], ResponseError>({
    queryKey: spaceQueryKeys.getAllByParams(params),
    queryFn: async () => {
      const response = await SpaceRequests.getSpaces({
        params,
        ...config,
      });
      return response.data;
    },
    ...options,
  });
};
